import React from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useMarketplaceStore } from "../../../../../store/useMarketplaceStore";
import { useTranslation } from "../../../../../hooks/useTranslation";
import LoadingButton from "@mui/lab/LoadingButton";

interface FormValues {
  asinList: string;
  skuPrefix: string;
}

interface AddProductDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AddProductDialog: React.FC<AddProductDialogProps> = ({
  open,
  onClose,
}) => {
  const {
    reset,
    register,
    watch,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      asinList: "",
      skuPrefix: "",
    },
  });

  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.inventoryManagement.inventory.actions.addNewProduct",
  );
  const { mutate, isLoading: isSaveLoading } = useCustomMutation();
  const invalidate = useInvalidate();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: FormValues) => {
    const asinArray = data.asinList
      .split("\n")
      .map((asin) => asin.trim())
      .filter((asin) => asin);

    mutate(
      {
        url: `store/${selectedCountry?.id}/product`,
        method: "post",
        errorNotification: () => ({
          message: translate("productsAddedError"),
          type: "error",
        }),
        values: {
          asinList: asinArray,
          skuPrefix: data.skuPrefix.trim(),
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: "all",
            invalidates: ["all"],
          });

          handleClose();
        },
      },
    );
  };

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{translate("title")}</DialogTitle>

        <DialogContent>
          <Typography variant="h6">{translate("asinList")}</Typography>

          <TextField
            placeholder={translate("enterAsinsOnePerLine")}
            multiline
            rows={8}
            fullWidth
            variant="outlined"
            {...register("asinList", {
              required: translate("asinListRequired"),
              validate: (value) =>
                value.split("\n").length <= 5000 ||
                translate("max5000AssinsRequired"),
            })}
            error={!!errors.asinList}
            helperText={errors.asinList?.message}
          />
          {(() => {
            const asinRegex = /^[A-Z0-9]{10}$/i;

            const count = (watch("asinList") ?? "")
              .split("\n")
              .map((x) => x.trim())
              .filter((x) => x !== "" && asinRegex.test(x)).length;

            return count > 0 ? (
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {translate("validAsins")}: {count}
              </Typography>
            ) : null;
          })()}
          <Typography variant="h6" mt={3}>
            {translate("SKUTitle")}
          </Typography>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            {...register("skuPrefix", {
              maxLength: {
                value: 20,
                message: translate("SKUPrefixError"),
              },
            })}
            error={!!errors.skuPrefix}
            helperText={errors.skuPrefix?.message}
          />

          <Typography variant="subtitle1" mt={1}>
            {translate("SKUPreview", {
              skuPrefix: watch("skuPrefix") ?? "XXXXX",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            {translateWithoutPrefix("buttons.cancel")}
          </Button>
          <LoadingButton
            loading={isSaveLoading}
            disabled={isSaveLoading ?? !isDirty}
            variant="contained"
            type="submit"
          >
            {translate("addToQueue")}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
